import { useAbility } from '@casl/react';
import {
  Autocomplete,
  capitalize,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useDashboard } from 'src/context/dashboardContext';
import {
  buildDashboardControlParams,
  parseDashboardControlParams,
  shouldHydrateDashboardZoneFromUrl,
} from 'src/features/dashboard/utils/dashboardUrlState';
import useApplicationGroups from 'src/hooks/useApplicationGroups';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useGetAllZonesQuery } from 'src/services/switch-rest-api/zone.service';
import {
  ALL_UTILITY_SELECTION_ID,
  isAllSelection,
  isUtilityType,
  isAllUtilitySelection,
} from 'src/utils/selection';
import { UtilityTypeBadge } from 'switch-fe-shared/components/Chips/UtilityBadge';
import {
  parseUtilityTypeOrAll,
  UtilityType,
  UtilityTypeOrAll,
} from 'switch-shared/modules/crud/utility/utility.enums';

const DashboardFilterBar = () => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    setUtility,
    utility,
    zone,
    setZone,
    setZoneCount,
    isAllGroupsSelected,
  } = useDashboard();
  const ability = useAbility(AbilityContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardUrlState = useMemo(
    () => parseDashboardControlParams(searchParams),
    [searchParams]
  );

  /* States */

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();
  const { applicationGroups } = useApplicationGroups();

  const appGroup = useMemo(() => {
    return applicationGroups.find((ag) => ag._id === appGroupId);
  }, [appGroupId, applicationGroups]);

  const utilityTypes = useMemo(() => {
    return appGroup?.utilityTypes ?? Object.values(UtilityType);
  }, [appGroup]);

  const utilityOptionsWithAll = useMemo(() => {
    const filteredTypes = utilityTypes.filter(
      (ele) => ele !== UtilityType.FIXED_FEE
    );

    // Only show "All Utilities" if there's more than one utility type
    if (filteredTypes.length === 1) {
      return filteredTypes;
    }

    return [ALL_UTILITY_SELECTION_ID as UtilityTypeOrAll, ...filteredTypes];
  }, [utilityTypes]);

  /* API Calls */

  const {
    data: zones,
    isLoading: isZonesLoading,
    isFetching: isZonesFetching,
  } = useGetAllZonesQuery(
    {
      applicationId: appId,
    },
    {
      skip:
        appId == null ||
        isAllSelection(appId) ||
        ability.cannot(ACTIONS.READ, 'Zones'),
    }
  );

  /* Functions & Functional Components */

  const previousUtilityCountRef = useRef<number | null>(null);
  const previousAppGroupIdRef = useRef<string | null>(null);

  useEffect(() => {
    setZoneCount(zones?.totalRecords ?? 0);
  }, [zones, setZoneCount]);

  useEffect(() => {
    if (!zones?.data) {
      return;
    }

    if (!shouldHydrateDashboardZoneFromUrl(dashboardUrlState.zoneId)) {
      return;
    }

    const selectedZone = zones.data.find(
      (zoneOption) => zoneOption._id === dashboardUrlState.zoneId
    );

    if (selectedZone && selectedZone._id !== zone?._id) {
      setZone(selectedZone);
    }
  }, [dashboardUrlState.zoneId, setZone, zone, zones]);

  // Auto-switch utility based on available utilities
  useEffect(() => {
    const filteredTypes = utilityTypes.filter(
      (ele) => ele !== UtilityType.FIXED_FEE
    );
    const currentUtilityCount = filteredTypes.length;
    const previousUtilityCount = previousUtilityCountRef.current;
    const previousAppGroupId = previousAppGroupIdRef.current;
    const hasGroupChanged =
      previousAppGroupId !== null && previousAppGroupId !== appGroupId;

    // If only one utility exists, switch to it
    if (currentUtilityCount === 1) {
      const singleUtility = filteredTypes[0];
      if (
        isUtilityType(utility) ||
        !filteredTypes.includes(utility as (typeof filteredTypes)[number])
      ) {
        setUtility(singleUtility);
      }
    }
    // If multiple utilities exist
    else if (currentUtilityCount > 1) {
      // Coming from a single-utility group -> switch to all
      if (previousUtilityCount === 1) {
        setUtility(ALL_UTILITY_SELECTION_ID);
      }
      // Switching groups into a multi-utility group -> default to all
      else if (hasGroupChanged && !isAllSelection(utility)) {
        setUtility(ALL_UTILITY_SELECTION_ID);
      }
      // Current utility is not in the list -> switch to all
      else if (
        isUtilityType(utility) &&
        !filteredTypes.includes(utility as (typeof filteredTypes)[number])
      ) {
        setUtility(ALL_UTILITY_SELECTION_ID);
      }
    }

    // Update the ref with current count
    previousUtilityCountRef.current = currentUtilityCount;
    previousAppGroupIdRef.current = appGroupId ?? null;
  }, [utilityTypes, utility, setUtility, appGroupId]);

  function getTitle(utilityTypeOrAll: UtilityTypeOrAll): string {
    if (isAllUtilitySelection(utilityTypeOrAll)) {
      if (isMobile) return 'All';
      return 'All Utilities';
    }
    return capitalize(utilityTypeOrAll);
  }

  // Hide filter bar when "All Groups" is selected
  if (isAllGroupsSelected) {
    return null;
  }

  return (
    <Grid container flexDirection={'row'} spacing={1}>
      <Grid item xs='auto'>
        <TextField
          value={
            utilityOptionsWithAll.includes(utility)
              ? utility
              : utilityOptionsWithAll[0]
          }
          onChange={(e) => {
            setUtility(parseUtilityTypeOrAll(e.target.value));
          }}
          label='Utility'
          fullWidth
          select
          size='small'
          InputLabelProps={{ sx: { background: 'transparent !important' } }}
          InputProps={{
            sx: { height: '38px', width: 'max-content' },
          }}
        >
          {utilityOptionsWithAll.map((utilityOption, index) => (
            <MenuItem key={index} value={utilityOption}>
              <Grid container gap={1} alignItems={'center'}>
                <Grid item>
                  <UtilityTypeBadge type={utilityOption} size='small' />
                </Grid>
                <Grid item xs>
                  <Typography>{getTitle(utilityOption)}</Typography>
                </Grid>
              </Grid>
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {!isAllSelection(appId) && !!zones?.data.length && (
        <Grid item xs='auto' minWidth={150}>
          <Autocomplete
            loading={isZonesFetching || isZonesLoading}
            disabled={isZonesFetching || isZonesLoading || !zones?.totalRecords}
            options={zones?.data ?? []}
            value={zone ?? null}
            onChange={(_, selectedZone) => {
              setZone(selectedZone ?? undefined);
              const nextParams = buildDashboardControlParams(searchParams, {
                utility,
                zoneId: selectedZone?._id,
              });

              if (nextParams.toString() !== searchParams.toString()) {
                setSearchParams(nextParams, { replace: true });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label='Zone'
                placeholder='All zones'
                sx={{
                  height: '100%',
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: theme.palette.text.primary,
                    opacity: 1,
                  },
                }}
                InputLabelProps={{
                  sx: { background: 'transparent !important' },
                }}
                InputProps={{
                  ...params.InputProps,
                  notched: true,
                  sx: { height: '100%' },
                  endAdornment: params.InputProps.endAdornment,
                }}
              />
            )}
            renderOption={(props, zoneOption) => (
              <MenuItem {...props} key={zoneOption._id}>
                {zoneOption.name}
              </MenuItem>
            )}
            getOptionLabel={(zoneOption) => zoneOption.name}
            sx={{ height: '38px' }}
            size='small'
          />
        </Grid>
      )}
    </Grid>
  );
};

export default DashboardFilterBar;
